import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useConfirm } from './ConfirmDialog';

const BASE = `${API_URL}/api/admin/regularisation`;
const eur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

function OrphanRow({ o, onAttach }) {
    const v = o.vehicule;
    return (
        <li className="border-t border-amber-100 pt-2 flex flex-wrap items-center gap-2" data-testid={`orphan-${o.fv_id}`}>
            <span className="flex-1 min-w-0">
                <b>{o.fournisseur || 'Fournisseur inconnu'}{o.numero_facture ? ` n°${o.numero_facture}` : ' (sans numéro)'}</b> — {eur(o.montant_ttc)} TTC — {o.immatriculation}
                {v ? <span className="block text-amber-800">→ {v.immatriculation} ({v.commercial}, vente {v.date_vente || '—'}) : marge {eur(v.marge_avant)} → {eur(v.marge_apres)}{v.deja_present ? ' — déjà présente' : ''}{v.mois_cloture ? ' — mois clôturé, à rouvrir d\'abord' : ''}</span>
                    : <span className="block text-red-700">{o.nb_vehicules_candidats ? 'Plusieurs véhicules possibles' : 'Aucun véhicule avec cette immatriculation'} — à traiter à la main</span>}
            </span>
            {v && !v.deja_present && !v.mois_cloture && <Button size="sm" variant="outline" className="h-8 text-xs bg-white" onClick={() => onAttach(o)} data-testid={`orphan-attach-${o.fv_id}`}>Rattacher</Button>}
        </li>
    );
}

function CopiesGroup({ g, onArchive }) {
    return (
        <li className="border-t border-gray-100 pt-2" data-testid={`copies-${g.principal.id}`}>
            <p><b>{g.principal.fournisseur} n°{g.principal.numero_facture}</b> — {eur(g.principal.montant_ttc)} — justificatif principal conservé (…{g.principal.id.slice(-6)})</p>
            <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="flex-1 text-gray-600">{g.copies.length} autre(s) photo(s) : {g.copies.map(c => `…${c.id.slice(-6)} (${c.statut})`).join(', ')}</span>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onArchive(g)} data-testid={`copies-archive-${g.principal.id}`}>Archiver les copies</Button>
            </div>
        </li>
    );
}

export function RegularisationPanel() {
    const [orphans, setOrphans] = useState([]);
    const [copies, setCopies] = useState([]);
    const [confirm, confirmDialog] = useConfirm();
    const load = useCallback(() => Promise.all([
        axios.get(`${BASE}/frais-orphelins`, { withCredentials: true }).then(r => setOrphans(r.data)),
        axios.get(`${BASE}/scans-redondants`, { withCredentials: true }).then(r => setCopies(r.data)),
    ]).catch(() => {}), []);
    useEffect(() => { load(); }, [load]);
    const attach = async (o) => {
        if (!(await confirm(`Rattacher ${o.fournisseur} n°${o.numero_facture} (${eur(o.montant_ttc)}) à ${o.vehicule.immatriculation} ?\nMarge ${eur(o.vehicule.marge_avant)} → ${eur(o.vehicule.marge_apres)}. Les commissions provisoires se recalculent, l'évolution est tracée.`, { confirmLabel: 'Rattacher' }))) return;
        try { await axios.post(`${BASE}/frais-orphelins/rattacher`, { fv_ids: [o.fv_id] }, { withCredentials: true }); toast.success('Dépense rattachée'); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    };
    const archive = async (g) => {
        if (!(await confirm(`Archiver ${g.copies.length} autre(s) photo(s) de la facture ${g.principal.numero_facture} ?\nRien n'est supprimé, restauration possible. Aucun montant ne change.`, { confirmLabel: 'Archiver' }))) return;
        try {
            const ids = g.copies.map(c => c.id);
            await axios.post(`${BASE}/scans-redondants/archiver`, { ids }, { withCredentials: true });
            toast.success('Copies archivées', { duration: 10000, action: { label: 'Restaurer', onClick: () => axios.post(`${BASE}/scans-redondants/restaurer`, { ids }, { withCredentials: true }).then(() => { toast.success('Copies restaurées'); load(); }) } });
            load();
        }
        catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    };
    if (!orphans.length && !copies.length) return null;
    return (
        <div className="rounded-xl border border-amber-200 bg-white p-4 space-y-3" data-testid="regularisation-panel">
            {confirmDialog}
            <p className="text-sm font-semibold text-gray-900">Régularisations à valider</p>
            {orphans.length > 0 && (
                <div className="rounded-lg bg-amber-50 p-3" data-testid="orphans-list">
                    <p className="text-xs font-semibold text-amber-900">Dépenses rattachées à un véhicule supprimé ({orphans.length})</p>
                    <ul className="text-xs text-amber-900 space-y-2 mt-2">{orphans.map(o => <OrphanRow key={o.fv_id} o={o} onAttach={attach} />)}</ul>
                </div>
            )}
            {copies.length > 0 && (
                <div className="rounded-lg bg-gray-50 p-3" data-testid="copies-list">
                    <p className="text-xs font-semibold text-gray-900">Factures photographiées plusieurs fois ({copies.length})</p>
                    <ul className="text-xs text-gray-800 space-y-2 mt-2">{copies.map(g => <CopiesGroup key={g.principal.id} g={g} onArchive={archive} />)}</ul>
                </div>
            )}
        </div>
    );
}
